import { useMemo } from 'react';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useGetBlocksQuery } from '../../../services/entities/impl/blfApi/endpoints/blocks';
import { useGetCustomersQuery } from '../../../services/entities/impl/blfApi/endpoints/customers';
import {
  useAddRanchMutation,
  useDeleteRanchMutation,
  useGetRanchesQuery,
  useUpdateRanchMutation,
} from '../../../services/entities/impl/blfApi/endpoints/ranches';
import { useGetAllScansQuery } from '../../../services/entities/impl/blfApi/endpoints/scans';
import { useGetUserMeQuery } from '../../../services/entities/impl/blfApi/endpoints/users';
import {
  BaseRanchSchema,
  RanchSchema,
  RegionEnum,
} from '../../../services/entities/impl/blfApi/schemas/ranch';
import { UserRolesEnum } from '../../../services/entities/impl/blfApi/schemas/user';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import {
  OnboardFormStepEnum,
  selectCurrOnboardRanchID,
  setCurrOnboardRanchID,
} from '../onboardingSlice';
import { DeleteWarningBase } from './components/DeleteWarningBase';
import { GenericForm, GenericFormProps } from './generic/GenericForm';
import { ExtraValidationFunction } from './generic/context/CRUDContext';
import { FormFieldType } from './generic/crud/components/FormFieldType';
import { checkFieldUniqueness } from './generic/crud/utils/checkFieldUniqueness';
import { extractIDFromResponse } from './generic/crud/utils/extractIDFromResponse';
import { toSelectOptions } from './generic/crud/utils/toSelectOptions';

const useGetCurrOnboardRanch = () => {
  const currOnboardRanchID = useAppSelector(selectCurrOnboardRanchID);
  // Using all users query to correctly handle user mutations.
  // Mostly because of correct handling of user roles mutations.
  const {
    currentData: currRanch,
    isLoading,
    isFetching,
  } = useGetRanchesQuery(undefined, {
    selectFromResult: ({ data, isLoading, isFetching }) => {
      return {
        currentData: data?.find((ranch) => ranch.id === currOnboardRanchID),
        isLoading,
        isFetching,
      };
    },
  });

  return {
    currentData: currOnboardRanchID ? currRanch || null : null,
    isLoading,
    isFetching,
  };
};

export const RanchForm: React.FC = () => {
  const dispatch = useAppDispatch();

  const { currentData: customers } = useGetCustomersQuery();
  const { currentData: ranches } = useGetRanchesQuery();
  const { currentData: blocks } = useGetBlocksQuery();
  const { currentData: scans } = useGetAllScansQuery();
  const { currentData: authorizedUser } = useGetUserMeQuery();
  const [handleAdd] = useAddRanchMutation();
  const [handleUpdate] = useUpdateRanchMutation();
  const [handleDelete] = useDeleteRanchMutation();
  const { currentData: currEntity } = useGetCurrOnboardRanch();
  const { t } = useTranslation();

  const deleteWarningProps = useMemo(() => {
    const warningText = t(
      'By deleting ranch all related items will be deleted as well.',
      { ns: 'common' }
    );

    if (!currEntity || !scans || !ranches || !blocks) {
      return { warningText };
    }
    const relatedBlocks = blocks.filter(
      (block) => block.ranchID === currEntity?.id
    );
    const relatedScans = scans.filter((scan) =>
      relatedBlocks.map((block) => block.id).includes(scan.blockID)
    );

    return {
      warningText,
      warnedChilds: {
        Blocks: {
          items: relatedBlocks,
        },
        Scans: {
          items: relatedScans,
        },
      },
    };
  }, [scans, currEntity]);

  const extraValidation: ExtraValidationFunction<
    yup.InferType<typeof BaseRanchSchema>
  > = (values) => {
    return checkFieldUniqueness({
      dataset: ranches,
      fieldsToCheck: ['name'],
      values: values,
      currentItemId: currEntity?.id,
      datasetFilter: (values, el) => values.customerID === el.customerID,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmitSuccess = async (res: any) => {
    const id = extractIDFromResponse(res);
    if (id) {
      dispatch(setCurrOnboardRanchID(id));
    }
  };

  const crudConfig: GenericFormProps<
    yup.InferType<typeof BaseRanchSchema>,
    yup.InferType<typeof RanchSchema>
  > = {
    handleAdd,
    handleUpdate,
    handleDelete,
    getCurrEntityHook: useGetCurrOnboardRanch,
    extraValidation,
    onSubmitSuccess,
    yupSchemaIn: BaseRanchSchema,
    yupSchemaOut: RanchSchema,
    slots: {
      [OnboardFormStepEnum.Add]: {
        props: {
          gridConfig: {
            numberOfColumns: 2,
          },
          fieldsConfig: {
            customerID: {
              type: FormFieldType.Select,
              label: t('Customer', { ns: ' common' }),
              initialValue: '',
              config: {
                items:
                  customers?.map((c) => ({ value: c.id, label: c.name })) || [],
              },
            },
            name: {
              type: FormFieldType.TextField,
              label: t('Name', { ns: 'common' }),
              initialValue: '',
            },
            region: {
              type: FormFieldType.Select,
              label: t('Region', { ns: 'common' }),
              initialValue: RegionEnum.northAmerica,
              config: {
                items: toSelectOptions(RegionEnum),
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.Edit]: {
        props: {
          gridConfig: {
            numberOfColumns: 2,
          },
          hiddenFields: ['id', 'fromDate', 'thruDate'],
          fieldsConfig: {
            id: {
              // hidden by hiddenFields config prop but needed to set the edit id
              type: FormFieldType.TextField,
              label: 'ID',
            },
            customerID: {
              type: FormFieldType.Select,
              label: t('Customer', { ns: ' common' }),
              initialValue: '',
              config: {
                items:
                  customers?.map((c) => ({ value: c.id, label: c.name })) || [],
              },
            },
            name: {
              type: FormFieldType.TextField,
              label: t('Name', { ns: 'common' }),
              initialValue: '',
            },
            region: {
              type: FormFieldType.Select,
              label: t('Region', { ns: 'common' }),
              initialValue: RegionEnum.northAmerica,
              config: {
                items: toSelectOptions(RegionEnum),
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.View]: {
        props: {
          hiddenFields: ['id', 'fromDate', 'thruDate'],
          fieldsConfig: {
            id: {
              type: FormFieldType.TextDisplay,
              label: t('ID'),
              initialValue: '',
            },
            customerID: {
              type: FormFieldType.TextDisplay,
              label: t('Customer', { ns: ' common' }),
              config: {
                valueGetter: (fieldProps) =>
                  customers?.find((c) => c.id === fieldProps.value)?.name || '',
              },
            },
            name: {
              type: FormFieldType.TextDisplay,
              label: t('Name', { ns: 'common' }),
              initialValue: '',
            },
            region: {
              type: FormFieldType.TextDisplay,
              label: t('Region', { ns: 'common' }),
              config: {
                valueGetter: (fieldProps) =>
                  toSelectOptions(RegionEnum).find(
                    (r) => r.value === fieldProps.value
                  )?.label || '',
              },
            },
          },
        },
      },
      [OnboardFormStepEnum.Delete]: {
        props: {
          submitCondition: () =>
            !authorizedUser?.roles
              .map((role) => role.name)
              .includes(UserRolesEnum.root),
          WarningElement: <DeleteWarningBase {...deleteWarningProps} />,
          hiddenFields: ['id', 'fromDate', 'thruDate'],
          fieldsConfig: {
            id: {
              type: FormFieldType.TextDisplay,
              label: t('ID'),
              initialValue: '',
            },
            customerID: {
              type: FormFieldType.TextDisplay,
              label: t('Customer', { ns: ' common' }),
              config: {
                valueGetter: (fieldProps) =>
                  customers?.find((c) => c.id === fieldProps.value)?.name || '',
              },
            },
            name: {
              type: FormFieldType.TextDisplay,
              label: t('Name', { ns: 'common' }),
              initialValue: '',
            },
            region: {
              type: FormFieldType.TextDisplay,
              label: t('Region', { ns: 'common' }),
              config: {
                valueGetter: (fieldProps) =>
                  toSelectOptions(RegionEnum).find(
                    (r) => r.value === fieldProps.value
                  )?.label || '',
              },
            },
          },
        },
      },
    },
  };
  return <GenericForm {...crudConfig} />;
};
